import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  IconButton,
  CircularProgress,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  selectMilestoneNotes,
  selectMilestoneNotesLoading,
  selectMilestoneNotesAddLoading,
} from "../../features/milestoneNotes/milestoneNotesSlice";
import {
  fetchMilestoneNotes,
  addMilestoneNote,
  deleteMilestoneNote,
} from "../../features/milestoneNotes/milestoneNotesApi";
import {
  selectUserProfile,
  selectAuthenticatedRole,
} from "../../features/authentication/authenticationSlice";

interface MilestoneNotesProps {
  milestoneId: number;
}

function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMinutes = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMinutes < 1) return "just now";
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 30) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

const MilestoneNotes = ({ milestoneId }: MilestoneNotesProps) => {
  const dispatch = useAppDispatch();
  const notes = useAppSelector(selectMilestoneNotes);
  const loading = useAppSelector(selectMilestoneNotesLoading);
  const addLoading = useAppSelector(selectMilestoneNotesAddLoading);
  const userProfile = useAppSelector(selectUserProfile);
  const userRole = useAppSelector(selectAuthenticatedRole);
  const [text, setText] = useState("");

  useEffect(() => {
    dispatch(fetchMilestoneNotes(milestoneId));
  }, [dispatch, milestoneId]);

  const handleAdd = async () => {
    if (!text.trim()) return;
    await dispatch(addMilestoneNote({ milestoneId, text: text.trim() }));
    setText("");
  };

  const handleDelete = (noteId: number) => {
    dispatch(deleteMilestoneNote(noteId));
  };

  const canDelete = (authorUserId: string) => {
    return userProfile.sub === authorUserId || userRole === "Admin";
  };

  return (
    <Card elevation={2} sx={{ borderRadius: 2 }}>
      <CardContent sx={{ p: 3, "&:last-child": { pb: 3 } }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Milestone Notes
        </Typography>

        <Box sx={{ display: "flex", gap: 1.5, mb: 3, alignItems: "flex-start" }}>
          <TextField
            multiline
            minRows={2}
            maxRows={4}
            size="small"
            placeholder="Add a note about this milestone..."
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
                handleAdd();
              }
            }}
            sx={{ flex: 1 }}
          />
          <Button
            variant="contained"
            size="small"
            onClick={handleAdd}
            disabled={addLoading || !text.trim()}
            sx={{ minWidth: 90, height: 40 }}
          >
            {addLoading ? <CircularProgress size={20} /> : "Add Note"}
          </Button>
        </Box>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={28} />
          </Box>
        ) : notes.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: "center" }}>
            No notes yet. Be the first to add one.
          </Typography>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {notes.map((note) => (
              <Box
                key={note.id}
                sx={{
                  py: 1.5,
                  px: 2,
                  backgroundColor: "rgba(0, 0, 0, 0.02)",
                  borderRadius: 1,
                  borderLeft: "3px solid rgba(54, 162, 235, 0.5)",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 0.5,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography variant="body2" fontWeight={600}>
                      {note.authorName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatRelativeTime(note.dateCreated)}
                    </Typography>
                  </Box>
                  {canDelete(note.authorUserId) && (
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(note.id)}
                      sx={{ color: "text.secondary", "&:hover": { color: "error.main" } }}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
                <Typography
                  variant="body2"
                  sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                >
                  {note.text}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default MilestoneNotes;
